/**
 * The individual pills of the desktop saved-view strip: the "All" pill, a
 * built-in view pill, a saved view pill, and the "More" pill that holds the
 * views the strip can't fit. Module-scope, memo-wrapped where they take
 * per-view props so a strip re-render doesn't re-render every pill.
 */

import { ChevronDown, List } from '@untitledui/icons';
import type { MouseEvent } from 'react';
import { memo, useCallback } from 'react';
import { Button as AriaButton } from 'react-aria-components';

import { Dropdown } from '@/lib/base/overlays/dropdown';
import type { BuiltInViewConfig } from '@/lib/crm/core/types';
import type { SavedFilterView } from '@/types/savedFilterView';
import { cx } from '@/utils/cx';

import { isModifiedClick } from './buildViewHref';
import {
  CARET_ICON_CLASS,
  DefaultOnlyMenuItem,
  DefaultViewMarker,
  DirtyDot,
  DirtyViewMenuItems,
  ManageSavedViewMenuItems,
  PILL_ACTIVE,
  PILL_BASE,
  PILL_INACTIVE,
  VIEW_ICON_CLASS,
} from './SavedViewMenuItems';
import {
  type DefaultViewRef,
  type DirtyViewActions,
  isDefaultRef,
  type StripItem,
} from './savedViewTypes';

const APPLY_CLASS =
  'inline-flex cursor-pointer items-center gap-1.5 outline-hidden';

// A native <a> loses react-aria's `data-focus-visible` ring, so every pill
// anchor gets an explicit keyboard focus ring instead — same token as the
// rest of lib/base (see e.g. HorizontalTabs, button-utility).
const VIEW_LINK_FOCUS =
  'rounded-md outline-focus-ring focus-visible:outline-2 focus-visible:outline-offset-2';

// Caret takes no space at rest (snug pill), appears on hover, and STAYS laid
// out while its menu is open via `aria-expanded:flex`. The latter is what keeps
// the popover anchored: without it, the mouse leaving the pill after opening
// would drop the caret back to `display:none`, giving react-aria a zero-size
// trigger to anchor against (it would fly to the top-left of the viewport).
// While the view is dirty (`data-dirty`) the caret stays showing: it is the
// way to Save / Discard, so it must be discoverable without a hover.
// Every caret carries `data-view-caret` so the strip can tell "a caret is
// showing" apart from other menu triggers (the More pill); the width a caret
// adds on hover is part of the strip's measurement budget (CARET_RESERVE in
// SavedViewStrip), so the growth shifts content right of the hovered pill
// but never pushes anything into the strip's clipped edge.
const CARET_CLASS =
  '-mr-0.5 hidden items-center justify-center rounded p-0.5 text-fg-quaternary transition-colors group-hover/pill:flex hover:text-fg-primary aria-expanded:flex data-[dirty]:flex';

// ---------------------------------------------------------------------------
// SavedViewPill
// ---------------------------------------------------------------------------

interface SavedViewPillProps {
  view: SavedFilterView;
  href: string;
  isActive: boolean;
  isDefault: boolean;
  /** True when this pill is active AND carries unsaved local changes. */
  isDirty: boolean;
  dirtyActions: DirtyViewActions;
  onApply: (viewId: string) => void;
  onRename: (view: SavedFilterView) => void;
  onToggleShare: (view: SavedFilterView) => void;
  onDelete: (view: SavedFilterView) => void;
  /**
   * Present only when the parent was given `onSetDefaultView`. Setting a
   * default is a personal preference, so unlike rename/share/delete it is
   * NOT gated on `view.is_owner` — it appears for org-shared views too.
   */
  onToggleDefault?: (view: SavedFilterView) => void;
  isMutationPending: boolean;
}

export const SavedViewPill = memo(function SavedViewPill({
  view,
  href,
  isActive,
  isDefault,
  isDirty,
  dirtyActions,
  onApply,
  onRename,
  onToggleShare,
  onDelete,
  onToggleDefault,
  isMutationPending,
}: SavedViewPillProps) {
  const handleApply = useCallback(() => {
    onApply(view.entity_id);
  }, [onApply, view.entity_id]);

  const handleRename = useCallback(() => {
    onRename(view);
  }, [onRename, view]);

  const handleToggleShare = useCallback(() => {
    onToggleShare(view);
  }, [onToggleShare, view]);

  const handleDelete = useCallback(() => {
    onDelete(view);
  }, [onDelete, view]);

  const handleToggleDefault = useCallback(() => {
    onToggleDefault?.(view);
  }, [onToggleDefault, view]);

  // Deliberately a native <a> + preventDefault here, NOT react-aria's
  // AriaLink/onPress: react-aria's press callbacks fire unconditionally on
  // modifier+left-click, which would apply the view in the current tab on
  // Cmd/Ctrl-click instead of letting the browser open a new tab. Plain
  // left-click stays SPA — the apply callback is authoritative (a bare
  // `?view=` URL change does not re-apply All/built-in views). Same pattern
  // in the other pill variants below and in SavedViewMenu.tsx.
  const handleAnchorClick = useCallback(
    (e: MouseEvent<HTMLAnchorElement>) => {
      if (isModifiedClick(e)) {
        return;
      }
      e.preventDefault();
      handleApply();
    },
    [handleApply]
  );

  // A dirty view always gets a caret — even a team-shared one the user can't
  // otherwise manage — because the menu is where Save / Discard live.
  const showCaret = view.is_owner || Boolean(onToggleDefault) || isDirty;

  return (
    <div className={cx(PILL_BASE, isActive ? PILL_ACTIVE : PILL_INACTIVE)}>
      <a
        href={href}
        onClick={handleAnchorClick}
        aria-current={isActive ? 'page' : undefined}
        className={cx(APPLY_CLASS, VIEW_LINK_FOCUS)}
      >
        <List className={VIEW_ICON_CLASS} />
        <span className='truncate'>{view.name}</span>
        {isDirty && <DirtyDot />}
      </a>

      {isDefault && <DefaultViewMarker />}

      {showCaret && (
        <Dropdown.Root>
          <AriaButton
            aria-label={`Manage ${view.name}`}
            data-view-caret=''
            data-dirty={isDirty || undefined}
            className={cx(
              CARET_CLASS,
              !isActive && 'opacity-0 group-hover/pill:opacity-100'
            )}
          >
            <ChevronDown className={CARET_ICON_CLASS} />
          </AriaButton>
          <Dropdown.Popover>
            <Dropdown.Menu disallowEmptySelection={false}>
              {isDirty && <DirtyViewMenuItems actions={dirtyActions} />}
              {isDirty && (view.is_owner || Boolean(onToggleDefault)) && (
                <Dropdown.Separator />
              )}
              <ManageSavedViewMenuItems
                view={view}
                isDefault={isDefault}
                showDefaultItem={Boolean(onToggleDefault)}
                isMutationPending={isMutationPending}
                onRename={handleRename}
                onToggleShare={handleToggleShare}
                onToggleDefault={handleToggleDefault}
                onDelete={handleDelete}
              />
            </Dropdown.Menu>
          </Dropdown.Popover>
        </Dropdown.Root>
      )}
    </div>
  );
});

// ---------------------------------------------------------------------------
// BuiltInViewPill
// ---------------------------------------------------------------------------

interface BuiltInViewPillProps {
  view: BuiltInViewConfig;
  href: string;
  isActive: boolean;
  isDefault: boolean;
  onSelect: (viewId: string) => void;
  /** Present only when the parent was given `onSetDefaultView`. */
  onToggleDefault?: (viewId: string) => void;
}

export const BuiltInViewPill = memo(function BuiltInViewPill({
  view,
  href,
  isActive,
  isDefault,
  onSelect,
  onToggleDefault,
}: BuiltInViewPillProps) {
  const handlePress = useCallback(() => {
    onSelect(view.id);
  }, [onSelect, view.id]);

  const handleToggleDefault = useCallback(() => {
    onToggleDefault?.(view.id);
  }, [onToggleDefault, view.id]);

  // Same native-<a> + modifier-guard pattern as SavedViewPill above.
  const handleAnchorClick = useCallback(
    (e: MouseEvent<HTMLAnchorElement>) => {
      if (isModifiedClick(e)) {
        return;
      }
      e.preventDefault();
      handlePress();
    },
    [handlePress]
  );

  // No default-view feature enabled for this pill — keep the original,
  // unwrapped rendering exactly as before.
  if (!isDefault && !onToggleDefault) {
    return (
      <a
        href={href}
        onClick={handleAnchorClick}
        aria-label={view.label}
        aria-current={isActive ? 'page' : undefined}
        className={cx(
          PILL_BASE,
          isActive ? PILL_ACTIVE : PILL_INACTIVE,
          VIEW_LINK_FOCUS
        )}
      >
        <List className={VIEW_ICON_CLASS} />
        {view.label}
      </a>
    );
  }

  return (
    <div className={cx(PILL_BASE, isActive ? PILL_ACTIVE : PILL_INACTIVE)}>
      <a
        href={href}
        onClick={handleAnchorClick}
        aria-label={view.label}
        aria-current={isActive ? 'page' : undefined}
        className={cx(APPLY_CLASS, VIEW_LINK_FOCUS)}
      >
        <List className={VIEW_ICON_CLASS} />
        {view.label}
      </a>

      {isDefault && <DefaultViewMarker />}

      {onToggleDefault && (
        <Dropdown.Root>
          <AriaButton
            aria-label={`Manage ${view.label}`}
            data-view-caret=''
            className={cx(
              CARET_CLASS,
              !isActive && 'opacity-0 group-hover/pill:opacity-100'
            )}
          >
            <ChevronDown className={CARET_ICON_CLASS} />
          </AriaButton>
          <Dropdown.Popover>
            <Dropdown.Menu disallowEmptySelection={false}>
              <DefaultOnlyMenuItem
                isDefault={isDefault}
                onToggleDefault={handleToggleDefault}
              />
            </Dropdown.Menu>
          </Dropdown.Popover>
        </Dropdown.Root>
      )}
    </div>
  );
});

// ---------------------------------------------------------------------------
// AllViewPill — the base "All" view (no filters / columns / sort)
// ---------------------------------------------------------------------------

interface AllViewPillProps {
  href: string;
  isActive: boolean;
  isDefault: boolean;
  /** Present only when the parent enabled the default-view feature. */
  onToggleDefault?: () => void;
  onSelect: () => void;
}

export const AllViewPill = memo(function AllViewPill({
  href,
  isActive,
  isDefault,
  onToggleDefault,
  onSelect,
}: AllViewPillProps) {
  // Same native-<a> + modifier-guard pattern as SavedViewPill above.
  const handleAnchorClick = useCallback(
    (e: MouseEvent<HTMLAnchorElement>) => {
      if (isModifiedClick(e)) {
        return;
      }
      e.preventDefault();
      onSelect();
    },
    [onSelect]
  );

  if (!isDefault && !onToggleDefault) {
    return (
      <a
        href={href}
        onClick={handleAnchorClick}
        aria-label='All'
        aria-current={isActive ? 'page' : undefined}
        className={cx(
          PILL_BASE,
          isActive ? PILL_ACTIVE : PILL_INACTIVE,
          VIEW_LINK_FOCUS
        )}
      >
        <List className={VIEW_ICON_CLASS} />
        All
      </a>
    );
  }

  return (
    <div className={cx(PILL_BASE, isActive ? PILL_ACTIVE : PILL_INACTIVE)}>
      <a
        href={href}
        onClick={handleAnchorClick}
        aria-label='All'
        aria-current={isActive ? 'page' : undefined}
        className={cx(APPLY_CLASS, VIEW_LINK_FOCUS)}
      >
        <List className={VIEW_ICON_CLASS} />
        All
      </a>

      {isDefault && <DefaultViewMarker />}

      {onToggleDefault && (
        <Dropdown.Root>
          <AriaButton
            aria-label='Manage all'
            data-view-caret=''
            className={cx(
              CARET_CLASS,
              !isActive && 'opacity-0 group-hover/pill:opacity-100'
            )}
          >
            <ChevronDown className={CARET_ICON_CLASS} />
          </AriaButton>
          <Dropdown.Popover>
            <Dropdown.Menu disallowEmptySelection={false}>
              <DefaultOnlyMenuItem
                isDefault={isDefault}
                onToggleDefault={onToggleDefault}
              />
            </Dropdown.Menu>
          </Dropdown.Popover>
        </Dropdown.Root>
      )}
    </div>
  );
});

// ---------------------------------------------------------------------------
// MoreViewsPill — the views the strip couldn't fit, as a menu. The active view
// is never in here (the strip pins it into the last visible slot), so the
// menu is purely a picker.
// ---------------------------------------------------------------------------

const DEFAULT_ADDON = 'Default';

interface MoreViewsPillProps {
  items: StripItem[];
  defaultViewRef: DefaultViewRef | null;
  hrefForItem: (item: StripItem) => string;
  onSelectAll: () => void;
  onSelectBuiltInView?: (viewId: string) => void;
  onSelectView: (viewId: string) => void;
}

function itemLabel(item: StripItem): string {
  switch (item.kind) {
    case 'all':
      return 'All';
    case 'builtin':
      return item.view.label;
    case 'saved':
      return item.view.name;
  }
}

function itemIsDefault(
  item: StripItem,
  defaultViewRef: DefaultViewRef | null
): boolean {
  switch (item.kind) {
    case 'all':
      return isDefaultRef(defaultViewRef, { kind: 'all' });
    case 'builtin':
      return isDefaultRef(defaultViewRef, {
        kind: 'builtin',
        id: item.view.id,
      });
    case 'saved':
      return isDefaultRef(defaultViewRef, {
        kind: 'saved',
        id: item.view.entity_id,
      });
  }
}

interface MoreViewsPillItemProps {
  item: StripItem;
  href: string;
  isDefault: boolean;
  dispatch: (item: StripItem) => void;
}

const MoreViewsPillItem = memo(function MoreViewsPillItem({
  item,
  href,
  isDefault,
  dispatch,
}: MoreViewsPillItemProps) {
  // Same native-<a> + modifier-guard pattern as SavedViewPill above.
  const handleClick = useCallback(
    (e: MouseEvent) => {
      if (isModifiedClick(e)) {
        return;
      }
      e.preventDefault();
      dispatch(item);
    },
    [item, dispatch]
  );

  const label = itemLabel(item);

  return (
    <Dropdown.Item
      id={item.key}
      href={href}
      onClick={handleClick}
      icon={List}
      label={label}
      textValue={label}
      addon={isDefault ? DEFAULT_ADDON : undefined}
    />
  );
});

export const MoreViewsPill = memo(function MoreViewsPill({
  items,
  defaultViewRef,
  hrefForItem,
  onSelectAll,
  onSelectBuiltInView,
  onSelectView,
}: MoreViewsPillProps) {
  const dispatch = useCallback(
    (item: StripItem) => {
      switch (item.kind) {
        case 'all':
          onSelectAll();
          break;
        case 'builtin':
          (onSelectBuiltInView ?? onSelectAll)(item.view.id);
          break;
        case 'saved':
          onSelectView(item.view.entity_id);
          break;
      }
    },
    [onSelectAll, onSelectBuiltInView, onSelectView]
  );

  return (
    <Dropdown.Root instance='more-views'>
      <AriaButton
        aria-label={`More views (${items.length})`}
        className={cx(PILL_BASE, PILL_INACTIVE, 'shrink-0')}
      >
        More
        <ChevronDown className={cx(CARET_ICON_CLASS, 'text-fg-quaternary')} />
      </AriaButton>
      <Dropdown.Popover placement='bottom left'>
        <Dropdown.Menu aria-label='More views' selectionMode='none'>
          {items.map(item => (
            <MoreViewsPillItem
              key={item.key}
              item={item}
              href={hrefForItem(item)}
              isDefault={itemIsDefault(item, defaultViewRef)}
              dispatch={dispatch}
            />
          ))}
        </Dropdown.Menu>
      </Dropdown.Popover>
    </Dropdown.Root>
  );
});
